import * as React from "react";
import { ReactElement, useMemo } from "react";
import { isNil } from "ramda";

import { Grid } from "affinityUI/elements/grid";
import { Loading } from "affinityUI/elements/loading";
import { EmptyState } from "affinityUI/features/emptyState";
import { Heading } from "affinityUI/structures/heading";
import { ConfirmatoryTestInfoTag } from "components/app/components/confirmatoryTestInfoTag";
import { Restricted } from "lib/auth";
import { PERMISSIONS } from "lib/biot/roles/types";
import type { PatientData } from "types/patient";

import { usePatient } from "../../../pages/patients/context";
import { useHospitalConfiguration } from "../../../pages/settings/context";

import { PatientDetails } from "./patientDetails";
import { ResultsTable } from "./resultsTable";

const WithDevice = () => (
  <>
    Patient has been issued a device, but has not tested yet.
    <br />
    Await patient&apos;s first test to see result history
  </>
);

const WithoutDevice = () => (
  <>
    Patient does not have a device and has no previous results. <br /> Request a device to get a patient started with
    Liberty
  </>
);

export const Patient: React.FC<{
  patient: PatientData;
  detailsActions?: Maybe<ReactElement>;
}> = ({ patient, detailsActions }) => {
  const { resultsLoading } = usePatient();

  const { testResults, enrollmentDate, disEnrollmentDate } = patient;
  const { hospitalConfig, normalRanges } = useHospitalConfiguration();

  const hasDevice = useMemo(
    () => !isNil(enrollmentDate) && isNil(disEnrollmentDate),
    [enrollmentDate, disEnrollmentDate]
  );

  const patientNormalRanges = useMemo<NormalRanges>(() => {
    const haemoglobinRange =
      patient.sex === "Female" ? normalRanges?.haemoglobinRangeFemale : normalRanges?.haemoglobinRangeMale;
    return { ...normalRanges, haemoglobinRange };
  }, [normalRanges, patient.sex]);

  const checkBelowThreshold = useMemo(
    () => testResults?.some(({ belowNeutrophilThreshold }) => belowNeutrophilThreshold),
    [testResults]
  );
  return (
    <Grid>
      <PatientDetails patient={patient} detailsActions={detailsActions} />
      <Grid $tight>
        <Heading>
          <Heading.SectionTitle>Result history</Heading.SectionTitle>
          {hospitalConfig?.confirmatoryThreshold && checkBelowThreshold ? (
            <Heading.Meta>
              <ConfirmatoryTestInfoTag confirmatoryThreshold={hospitalConfig.confirmatoryThreshold} />
            </Heading.Meta>
          ) : null}
        </Heading>
        <Restricted
          permits={[PERMISSIONS.RESULT_DATA_VIEW]}
          fallback={
            <EmptyState>
              <EmptyState.Message>You do not have permission to view results</EmptyState.Message>
            </EmptyState>
          }
        >
          {resultsLoading ? (
            <Loading />
          ) : testResults?.length ? (
            <ResultsTable
              confirmatoryThreshold={hospitalConfig?.confirmatoryThreshold || null}
              patientNormalRanges={patientNormalRanges}
              results={testResults}
            />
          ) : (
            <EmptyState>
              <EmptyState.Message>{hasDevice ? <WithDevice /> : <WithoutDevice />}</EmptyState.Message>
            </EmptyState>
          )}
        </Restricted>
      </Grid>
    </Grid>
  );
};
